import {message, useIntl, type MessageDescriptor} from '@sail/intl';
import type {StepStatus} from 'src/v2/components/common/StepProgressIndicator';
import {useSelector} from 'src/v2/state/store';
import {useGetFailureSubtitle} from 'src/v2/components/anyCrypto/progress/useGetFailureSubtitle';
import {REFUNDABLE_REASONS} from 'src/v2/components/anyCrypto/relayConstants';
import {
  NETWORK_TIMING,
  DEFAULT_TIMING,
  formatTimingRange,
} from 'src/v2/components/anyCrypto/networkTiming';
import {
  getVariant,
  VARIANT_STYLES,
  type Variant,
  type VariantStyles,
} from 'src/v2/components/anyCrypto/progress/progressVariant';
import {
  getStepStatuses,
  getRefundStepStatus,
} from 'src/v2/components/anyCrypto/progress/stepStatus';

const messages = {
  processingTitle: message({
    id: 'crypto.payins.v2.any_crypto.processing.title',
    description: 'Title for relay payment processing screen',
    defaultMessage: 'Your payment is being processed.',
  }),

  processingSubtitle: message<{
    processingTime: string;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.subtitle',

    description:
      'Subtitle for relay payment processing screen with network-specific time estimate',

    defaultMessage:
      "It usually takes {processingTime}. You can safely close this window. We'll continue processing in the background.",
  }),

  successTitle: message({
    id: 'crypto.payins.v2.any_crypto.processing.success_title',
    description: 'Title when relay payment is complete',
    defaultMessage: 'Your payment is complete.',
  }),

  failedTitle: message({
    id: 'crypto.payins.v2.any_crypto.processing.failed_title',
    description: 'Title when relay payment has failed',
    defaultMessage: 'Payment failed.',
  }),

  step1Label: message({
    id: 'crypto.payins.v2.any_crypto.processing.step1',
    description: 'Label for deposit detecting step',
    defaultMessage: 'Payment detection',
  }),

  step2Label: message({
    id: 'crypto.payins.v2.any_crypto.processing.step2',
    description: 'Label for deposit delivering step',
    defaultMessage: 'Payment processing',
  }),

  step2FailedLabel: message({
    id: 'crypto.payins.v2.any_crypto.processing.step2_failed',
    description: 'Label for failed deposit delivery step',
    defaultMessage: 'Payment not delivered',
  }),

  step3RefundIssuingLabel: message({
    id: 'crypto.payins.v2.any_crypto.processing.step3_refund_issuing',
    description: 'Label for refund issuing step',
    defaultMessage: 'Refund issuing',
  }),

  step3RefundNotIssuedLabel: message({
    id: 'crypto.payins.v2.any_crypto.processing.step3_refund_not_issued',
    description: 'Label for refund not issued step due to VASP',
    defaultMessage: 'Refund not issued',
  }),
};

const TITLE_BY_VARIANT: Record<Variant, MessageDescriptor> = {
  processing: messages.processingTitle,
  success: messages.successTitle,
  failed: messages.failedTitle,
};

type Step = {label: string; status: StepStatus};

export type ProgressScreenContent = {
  statusIconVariant: VariantStyles;
  title: string;
  subtitle: React.ReactNode;
  steps: Step[];
};

// Derives the icon, title, subtitle, and step list the progress screen renders
// from the current relay/session state.
export const useProgressScreenContent = (): ProgressScreenContent => {
  const intl = useIntl();

  const sessionState = useSelector((state) => state.state);
  const maxRelayRequestStatus = useSelector(
    (state) => state.anyCryptoDeposit.maxRelayRequestStatus,
  );
  const relayFailReason = useSelector(
    (state) => state.anyCryptoDeposit.relayFailReason,
  );
  const relayRefundFailReason = useSelector(
    (state) => state.anyCryptoDeposit.relayRefundFailReason,
  );
  const selectedNetwork = useSelector(
    (state) => state.anyCryptoDeposit.selectedNetwork,
  );
  const walletConnectEnabled = useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto_wallet_connect,
  );
  const failureSubtitle = useGetFailureSubtitle();

  const variant = getVariant(maxRelayRequestStatus, sessionState);
  const stepStatuses = getStepStatuses(maxRelayRequestStatus, sessionState);

  // The non-relay wallet-connect pay flow reaches this screen already at
  // fulfillment_complete with no relay request driving the payment, so
  // maxRelayRequestStatus is null (or transiently 'waiting'). Relay-funded
  // payments only complete with a 'success' status, so they keep the steps.
  const isNonRelayWalletConnect =
    walletConnectEnabled &&
    sessionState === 'fulfillment_complete' &&
    (maxRelayRequestStatus === null || maxRelayRequestStatus === 'waiting');

  const timing = selectedNetwork
    ? NETWORK_TIMING[selectedNetwork] ?? DEFAULT_TIMING
    : DEFAULT_TIMING;
  const processingTime = formatTimingRange(timing.processingTime, intl);

  let subtitle: React.ReactNode;
  if (variant === 'processing') {
    subtitle = intl.formatMessage(messages.processingSubtitle, {
      processingTime,
    });
  } else if (variant === 'failed') {
    subtitle = failureSubtitle;
  }

  const isRefundable =
    variant === 'failed' && REFUNDABLE_REASONS.has(relayFailReason ?? '');
  const isBlockedRefundToVasp =
    relayRefundFailReason === 'REFUND_RECIPIENT_IS_VASP';

  const step2Label =
    variant === 'failed' ? messages.step2FailedLabel : messages.step2Label;
  const refundStepLabel = isBlockedRefundToVasp
    ? messages.step3RefundNotIssuedLabel
    : messages.step3RefundIssuingLabel;

  // The wallet-connect completion shows only the balance subtitle, no steps.
  const steps: Step[] = isNonRelayWalletConnect
    ? []
    : [
        {
          label: intl.formatMessage(messages.step1Label),
          status: stepStatuses[0],
        },
        {label: intl.formatMessage(step2Label), status: stepStatuses[1]},
        ...(isRefundable
          ? [
              {
                label: intl.formatMessage(refundStepLabel),
                status: isBlockedRefundToVasp
                  ? 'failed'
                  : getRefundStepStatus(maxRelayRequestStatus),
              },
            ]
          : []),
      ];

  return {
    statusIconVariant: VARIANT_STYLES[variant],
    title: intl.formatMessage(TITLE_BY_VARIANT[variant]),
    subtitle,
    steps,
  };
};
